/**
 * SectionHeader — the title row at the top of every equipment panel.
 *
 * Layout:  [Title]                    [counts] [actions...]
 *          --------- 1px border bottom ---------
 *
 * The counts strip ("X avail · Y out · Z OOS") and the actions slot are
 * both optional, so this header is reusable for every panel from
 * Security Keys (no actions) to Officer Radios (full add/OOS controls).
 */
import type { ReactNode } from 'react'

interface CountSpec {
  available?: number
  out?: number
  oos?: number
}

interface SectionHeaderProps {
  title: string
  counts?: CountSpec
  actions?: ReactNode
}

export default function SectionHeader({ title, counts, actions }: SectionHeaderProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16,
        paddingBottom: 8,
        borderBottom: '1px solid var(--border)',
      }}
    >
      <h3 style={{ fontSize: 22, fontWeight: 700, color: '#1d4ed8', margin: 0 }}>
        {title}
      </h3>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {counts && (counts.available != null || counts.out != null || counts.oos != null) && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {counts.available != null && (
              <>
                <span style={{ color: '#22c55e', fontWeight: 700 }}>{counts.available}</span> avail
              </>
            )}
            {counts.out != null && (
              <>
                {' · '}
                <span style={{ color: '#f97316', fontWeight: 700 }}>{counts.out}</span> out
              </>
            )}
            {counts.oos != null && counts.oos > 0 && (
              <>
                {' · '}
                <span style={{ color: '#ef4444', fontWeight: 700 }}>{counts.oos}</span> OOS
              </>
            )}
          </span>
        )}
        {actions}
      </div>
    </div>
  )
}
